import { useState, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import { Box, CircularProgress, IconButton, Typography } from '../../ui';
import { ChevronLeftIcon, ChevronRightIcon } from '../../icons';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

interface PDFViewerProps {
  fileUrl: string;
  sx?: any;
  onViewedFor10Seconds?: () => void;
}

export default function PDFViewer({ fileUrl, sx, onViewedFor10Seconds }: PDFViewerProps) {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // 10-second viewing timer
  useEffect(() => {
    if (!onViewedFor10Seconds) return;

    const timer = setTimeout(() => {
      console.log('PDF viewed for 10 seconds');
      onViewedFor10Seconds();
    }, 10000);

    return () => clearTimeout(timer);
  }, [onViewedFor10Seconds]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', ...sx }}>
      {/* PDF Document */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
          minHeight: '600px',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6 }}>
            <CircularProgress size={50} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading PDF...
            </Typography>
          </Box>
        )}
        <Document
          file={fileUrl}
          onLoadSuccess={({ numPages }) => {
            setNumPages(numPages);
            setPageNumber(1);
            setLoading(false);
          }}
          onLoadError={(error) => {
            console.error('Error loading PDF:', error);
            setLoading(false);
          }}
          loading=""
          onLoadStart={() => setLoading(true)}
        >
          {!loading && <Page pageNumber={pageNumber} width={900} renderTextLayer={true} renderAnnotationLayer={true} />}
        </Document>
      </Box>

      {/* Pagination Controls */}
      {numPages && numPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            size="small"
            onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
            disabled={pageNumber === 1}
            color="primary"
          >
            <ChevronLeftIcon />
          </IconButton>

          <Typography variant="body2" color="text.secondary">
            Page {pageNumber} of {numPages}
          </Typography>

          <IconButton
            size="small"
            onClick={() => setPageNumber((prev) => Math.min(prev + 1, numPages))}
            disabled={pageNumber === numPages}
            color="primary"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
